import { useState, useEffect } from 'react';
import { postRequest } from '../../utils/networking';
import { apiUrls } from '../../utils/url';
import { toast } from 'react-toastify';
import { Search, X, Loader2, CreditCard, History } from 'lucide-react';
import { IndianRupee } from '../../components/CustomIcons';
import Pagination from '../../components/Pagination';
import CommonToastContainer from '../../components/CommonToastContainer';
import NoDataFound from '../../components/NoDataFound';

export default function WebinarPaymentHistory() {
    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);
    const [fromDate, setFromDate] = useState(formatDateForInput(lastMonth));
    const [toDate, setToDate] = useState(formatDateForInput(today));

    const fetchPaymentHistory = async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select both from and to dates');
            return;
        }
        setIsLoading(true);
        try {
            const payload = {
                fromdate: fromDate,
                todate: toDate
            };
            const response = await postRequest(apiUrls.getwebinarorderbydate, payload);
            console.log('Webinar payment history response:', response);

            if (response.status === 'S' && response.data) {
                setPayments(Array.isArray(response.data) ? response.data : []);
            } else {
                setPayments([]);
            }
        } catch (err) {
            console.error('Error fetching webinar payment history:', err);
            toast.error('Unable to load payment history. Please try again later.');
            setPayments([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPaymentHistory();
    }, []);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
    };

    const filteredPayments = payments.filter(payment => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            (payment.paymentid && payment.paymentid.toLowerCase().includes(searchLower)) ||
            (payment.webinarname && payment.webinarname.toLowerCase().includes(searchLower)) ||
            (payment.webinartitle && payment.webinartitle.toLowerCase().includes(searchLower)) ||
            (payment.username && payment.username.toLowerCase().includes(searchLower)) ||
            (payment.email && payment.email.toLowerCase().includes(searchLower)) ||
            (payment.contact && payment.contact.includes(searchLower)) ||
            (payment.method && payment.method.toLowerCase().includes(searchLower))
        );
    });

    const totalItems = filteredPayments.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentPayments = filteredPayments.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        
        // Handle Unix timestamp (often used in Razorpay) or ISO string
        const date = typeof dateValue === 'number' && dateValue > 10000000000 ? new Date(dateValue) : 
                     typeof dateValue === 'number' ? new Date(dateValue * 1000) : 
                     new Date(dateValue);
                     
        return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null) return '0.00';
        // Razorpay amounts are usually in paise
        const displayAmount = amount > 1000 ? amount / 100 : amount;
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(displayAmount);
    };

    return (
        <div className="bg-[#F8F9FE] text-zinc-900 antialiased h-full overflow-hidden">
            <main className="max-w-[1400px] mx-auto h-full flex flex-col px-4 py-6 w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                    {/* Date Filters */}
                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="flex-1 min-w-[120px] px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                        <span className="text-gray-500 shrink-0">to</span>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="flex-1 min-w-[120px] px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                        <button
                            onClick={fetchPaymentHistory}
                            className="w-full sm:w-auto px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-xl transition-colors"
                        >
                            Filter
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className="relative w-full sm:w-96">
                        <input
                            type="text"
                            placeholder="Search by Payment ID, Webinar, Email..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        {searchTerm && (
                            <button
                                onClick={clearSearch}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="bg-white/70 rounded-xl border border-zinc-100  flex-1 flex flex-col min-h-0 overflow-hidden">
                    <div className="overflow-auto flex-1 flex flex-col min-h-0">
                        <div className="min-w-[1100px] flex-1 flex flex-col min-h-0">
                            {/* Header */}
                            <div className="sticky top-0 z-10 grid grid-cols-[60px_1fr_1.5fr_1.5fr_100px_100px_110px_1.5fr_160px] gap-4 px-6 py-4 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200 text-sm font-semibold text-zinc-600 capitalize">
                                <div className="flex items-center justify-center">S.No</div>
                                <div>Username</div>
                                <div>Webinar Name</div>
                                <div>Payment ID</div>
                                <div className="flex items-center justify-center">Amount</div>
                                <div className="flex items-center justify-center">Method</div>
                                <div className="flex items-center justify-center">Status</div>
                                <div>Contact Info</div>
                                <div className="flex items-center justify-center">Date & Time</div>
                            </div>

                            {/* Body */}
                            <div className="flex-1 relative min-h-[400px]">
                                {isLoading ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                                        <div className="text-center">
                                            <Loader2 className="h-10 w-10 animate-spin text-blue-500 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">Loading payment records...</p>
                                        </div>
                                    </div>
                                ) : currentPayments.length > 0 ? (
                                    currentPayments.map((pay, index) => (
                                        <div
                                            key={pay.id || index}
                                            className="grid grid-cols-[60px_1fr_1.5fr_1.5fr_100px_100px_110px_1.5fr_160px] gap-4 py-4 px-6 border-b border-zinc-100 hover:bg-zinc-50 transition-colors cursor-default items-center"
                                        >
                                            <div className="text-sm font-semibold text-zinc-500 flex items-center justify-center">
                                                {startIndex + index + 1}
                                            </div>
                                            <div className="text-sm font-medium text-zinc-900 truncate" title={pay.username}>
                                                {pay.username || '-'}
                                            </div>
                                            <div className="text-sm font-medium text-zinc-900 truncate" title={pay.webinarname || pay.webinartitle}>
                                                {pay.webinarname || pay.webinartitle || '-'}
                                            </div>
                                            <div className="text-sm font-mono text-zinc-600 truncate" title={pay.paymentid}>
                                                {pay.paymentid || '-'}
                                            </div>
                                            <div className="text-sm font-bold text-zinc-800 flex items-center justify-center gap-0.5">
                                                <IndianRupee className="w-3.5 h-3.5 text-zinc-800" />
                                                {formatAmount(pay.amount)}
                                            </div>
                                            <div className="text-sm text-zinc-600 capitalize flex items-center justify-center gap-1.5">
                                                <CreditCard className="w-3.5 h-3.5 text-zinc-400" />
                                                {pay.method || '-'}
                                            </div>
                                            <div className="flex items-center justify-center">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    pay.status === 'captured' || pay.status === 'success' || pay.status === 'S' 
                                                        ? 'bg-green-100 text-green-700' 
                                                        : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {pay.status || 'Pending'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <div className="text-xs font-medium text-zinc-900 truncate">{pay.email}</div>
                                                <div className="text-[10px] text-zinc-500">{pay.contact}</div>
                                            </div>
                                            <div className="text-xs text-zinc-600 flex items-center justify-center">
                                                {formatDate(pay.createdon || pay.createdat || pay.created_at)}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <NoDataFound searchTerm={searchTerm} />
                                )}
                            </div>
                        </div>
                    </div>

                    {totalItems > 0 && !isLoading && (
                        <div className="mt-auto border-t border-zinc-100 bg-white/50">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                itemsPerPage={itemsPerPage}
                                totalItems={totalItems}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </div>
                    )}
                </div>
            </main>
            <CommonToastContainer />
        </div>
    );
}
